/**
 * ring-toss.js — GDD 6.3.1.
 *
 * Three rows of bottles at 2 m / 3 m / 4 m and five light plastic rings. A ring
 * that comes to rest flat over a bottle neck scores by row: 50 near, 150 mid,
 * 500 far. Mid and far also award a plush on the spot (GDD 7.4), and a round
 * can win several — each landing is judged on its own.
 *
 * Two things make the "did it go over the neck?" question honest:
 *
 *   1. The bottle's solid geometry narrows to a cap of radius
 *      `ringInnerRadius − neckRadius` (2.25 cm). A ring is a solid disc in the
 *      greybox physics, so it cannot literally drop over a neck — but a disc
 *      can only balance on that cap if its centre is somewhere a real ring
 *      would have encircled the neck from. Land wider and it catches the
 *      shoulder and slides off.
 *   2. The landing test waits for the ring to actually come to REST. Rings
 *      bounce off bottles and skitter across the table constantly; judging a
 *      moving ring would score glancing hits.
 *
 * Floaty feel comes from gravityScale 0.8 plus 0.6 linear damping on the ring
 * bodies (GDD 5.4, 6.3.1) — a ring hangs in the air long enough that the throw
 * is an arc rather than a line.
 *
 * @module stalls/ring-toss
 */

import * as THREE from 'three';
import { RING_TOSS, PRIZE_KIND, PALETTE, HUB } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';

const CFG = RING_TOSS;
const COS_MAX_TILT = Math.cos((CFG.landMaxTiltDeg * Math.PI) / 180);

/**
 * The factory's ring is a Mesh carrying its own `rotation.x = π/2`, which lays
 * the torus flat so it agrees with the +Y axis of its cylinder collider.
 * `spawn()` overwrites a dynamic mesh's quaternion with the body pose, which
 * would stand that torus on edge while the collider stayed flat — a ring that
 * renders vertical and behaves horizontal. Wrapping it in a group fixes the
 * ownership: the group takes the body pose, the torus keeps its own rotation.
 *
 * @param {number} color
 * @returns {THREE.Group}
 */
function makeRing(color) {
  const torus = factory.createRing(color);
  const g = new THREE.Group();
  g.name = 'ring';
  g.add(torus);
  g.userData.collider = torus.userData.collider;
  g.userData.innerRadius = torus.userData.innerRadius;
  return g;
}

class RingToss extends StallBase {
  constructor(ctx) {
    super(ctx);
    /**
     * @type {Array<{ id: string, row: object, local: THREE.Vector3, mesh: THREE.Object3D,
     *                body: object, cap: object, ringed: boolean }>}
     */
    this.bottles = [];
    /** @type {Array<{ handle: object, age: number, restTimer: number, resolved: boolean }>} */
    this.rings = [];
    /** @type {Array<(dt: number) => boolean>} */
    this._effects = [];
    this._settleTimer = 0;
    this._scratchQ = new THREE.Quaternion();
    this._scratchUp = new THREE.Vector3();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this._buildFloor();
    this._buildTable();

    // Small price cards standing on the table BESIDE each row. Hung under the
    // table edge — the obvious place — they are invisible behind the table top;
    // centred in front of a row at a legible size they hide the bottles.
    const cardBg = { near: '#123a52', mid: '#4a3a12', far: '#4a1a16' };
    for (const row of CFG.rows) {
      const label = factory.createSign('', {
        w: 0.38, h: 0.12,
        lines: [`${row.points} PTS`],
        bg: cardBg[row.key],
      });
      label.position.set(-0.95, CFG.tableHeight + 0.065, row.distance);
      label.rotation.y = Math.PI;
      this.root.add(label);
    }

    // Within the 0.45 m grab radius of the desktop hand, which sits around
    // (±0.28, 1.34, 0.45) in this space.
    this.buildTray({ width: 0.8, y: 1.20, z: 0.72 });
    this.buildScoreboard({ y: 2.3, z: 3.6 });

    const light = createStallLight(0xdcefff, 2.0, 11);
    light.position.set(0, 3.0, 2.6);
    this.root.add(light);
  }

  /** @private */
  _buildFloor() {
    const w = HUB.boothWidth - 0.4;
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(w, 0.1, 6.4),
      factory.mat(0x5c6a7a, { rough: 1 }),
    );
    floor.receiveShadow = true;
    this.spawn(floor, { x: 0, y: -0.05, z: 2.6 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: w / 2, y: 0.05, z: 3.2 },
      position: this.localToWorld(0, -0.05, 2.6),
      friction: 0.85, restitution: 0.1,
    });

    // Rings that sail long have to stop somewhere other than the midway.
    const backstop = new THREE.Mesh(
      new THREE.BoxGeometry(w, 2.2, 0.1),
      // Light enough to read as a backdrop. A dark slab this size looks like a
      // hole punched in the back of the booth.
      factory.mat(0x9c8fae, { rough: 0.95 }),
    );
    backstop.receiveShadow = true;
    this.spawn(backstop, { x: 0, y: 1.1, z: 5.4 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: w / 2, y: 1.1, z: 0.05 },
      position: this.localToWorld(0, 1.1, 5.4),
      restitution: 0.05, friction: 0.9,
    });
  }

  /** @private */
  _buildTable() {
    const top = new THREE.Mesh(
      new THREE.BoxGeometry(CFG.tableWidth, 0.08, CFG.tableHalfDepth * 2),
      factory.mat(PALETTE.wood, { rough: 0.9 }),
    );
    top.receiveShadow = true;
    this.spawn(top, { x: 0, y: CFG.tableHeight - 0.04, z: CFG.tableZ }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: CFG.tableWidth / 2, y: 0.04, z: CFG.tableHalfDepth },
      position: this.localToWorld(0, CFG.tableHeight - 0.04, CFG.tableZ),
      // Low restitution: a ring that misses should die on the cloth rather
      // than ping back off the table and land on a bottle by accident.
      friction: 0.75, restitution: 0.08,
    });

    // A lip so missed rings stay on the table instead of rolling into the hub.
    for (const side of [-1, 1]) {
      const lip = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.09, CFG.tableHalfDepth * 2),
        factory.mat(PALETTE.boothFrame, { rough: 0.9 }),
      );
      this.spawn(lip, { x: side * CFG.tableWidth / 2, y: CFG.tableHeight + 0.04, z: CFG.tableZ }, {
        type: 'fixed', shape: 'box',
        halfExtents: { x: 0.03, y: 0.045, z: CFG.tableHalfDepth },
        position: this.localToWorld(side * CFG.tableWidth / 2, CFG.tableHeight + 0.04, CFG.tableZ),
        friction: 0.6, restitution: 0.1,
      });
    }
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.ringsPerRound });
    this.bottles = [];
    this.rings = [];
    this._effects = [];
    this._settleTimer = 0;

    for (const row of CFG.rows) this._buildRow(row);

    let i = 0;
    const handles = this.dispense(
      CFG.ringsPerRound,
      () => makeRing(CFG.ringColors[i++ % CFG.ringColors.length]),
      {
        y: 1.26, z: 0.72, spacing: 0.155,
        desc: {
          mass: CFG.ringMass,
          gravityScale: CFG.ringGravityScale,
          linearDamping: CFG.ringLinearDamping,
          angularDamping: CFG.ringAngularDamping,
          restitution: CFG.ringRestitution,
          ccd: true,
        },
      },
    );
    for (const handle of handles) {
      this.rings.push({ handle, age: 0, restTimer: 0, resolved: false });
    }
  }

  /**
   * Stand one row of bottles on the table.
   * @param {{key: string, distance: number, bottles: number, spacing: number}} row
   * @private
   */
  _buildRow(row) {
    const baseY = CFG.tableHeight;
    for (let i = 0; i < row.bottles; i++) {
      const x = (i - (row.bottles - 1) / 2) * row.spacing;
      const local = new THREE.Vector3(x, baseY, row.distance);

      const mesh = factory.createBottle();
      // The glass body: a solid cylinder from the table up to bodyTopY.
      const body = this.spawn(mesh, local, {
        type: 'fixed', shape: 'cylinder',
        radius: CFG.bottleBodyRadius, halfHeight: CFG.bottleBodyTopY / 2,
        position: this.localToWorld(x, baseY + CFG.bottleBodyTopY / 2, row.distance),
        friction: 0.5, restitution: 0.2,
      });

      // The catch a ring can actually settle on. Its radius is the exact
      // off-centre distance at which a real ring still clears the neck, so
      // anything balanced here has genuinely gone "over the neck".
      const capHalf = (CFG.bottleCapTopY - CFG.bottleBodyTopY) / 2;
      const cap = this.spawn(new THREE.Group(), local, {
        type: 'fixed', shape: 'cylinder',
        radius: CFG.bottleCapRadius, halfHeight: capHalf,
        position: this.localToWorld(x, baseY + CFG.bottleBodyTopY + capHalf, row.distance),
        friction: 0.7, restitution: 0.05,
      });

      const id = `${row.key}-${i}`;
      this.registerTarget(body, { id, group: row.key });
      this.bottles.push({ id, row, local, mesh, body, cap, ringed: false });
    }
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._trackRings(dt);
    this._runEffects(dt);
    this._checkRoundEnd(dt);
  }

  /**
   * A thrown ring is judged once it stops moving — bouncing rings skitter
   * across bottle necks constantly and judging one in flight would score
   * glancing contact as a landing.
   * @private
   */
  _trackRings(dt) {
    for (const ring of this.rings) {
      if (ring.resolved) continue;
      const h = ring.handle;
      if (h.removed) { ring.resolved = true; continue; }
      if (!h.userData.thrown) continue;

      ring.age += dt;
      const v = h.velocity();
      const w = h.body.angvel();
      const still = v.lengthSq() < CFG.restSpeed * CFG.restSpeed
        && (w.x * w.x + w.y * w.y + w.z * w.z) < CFG.restAngular * CFG.restAngular;
      ring.restTimer = still ? ring.restTimer + dt : 0;

      if (ring.restTimer >= CFG.restTime || ring.age >= CFG.ringLifetime) {
        this._resolveRing(ring);
      }
    }
  }

  /**
   * Decide whether a ring that has come to rest landed on a bottle.
   * @private
   */
  _resolveRing(ring) {
    ring.resolved = true;
    const h = ring.handle;
    const world = h.position();
    const local = this.worldToLocal(world);
    const bottle = this._landedBottle(h, local);

    if (!bottle) {
      h.freeze();
      return;
    }
    this._onRingLanded(ring, bottle);
  }

  /**
   * @param {object} handle
   * @param {THREE.Vector3} local Ring centre in stall-local space.
   * @returns {object|null} The bottle it landed on.
   * @private
   */
  _landedBottle(handle, local) {
    // "Landed cleanly OVER a neck" means flat. The plot is yaw-only, so local
    // up and world up are the same axis and the body quaternion is enough.
    const r = handle.body.rotation();
    this._scratchQ.set(r.x, r.y, r.z, r.w);
    const tilt = Math.abs(this._scratchUp.set(0, 1, 0).applyQuaternion(this._scratchQ).y);
    if (tilt < COS_MAX_TILT) return null;

    for (const b of this.bottles) {
      if (b.ringed) continue;
      const dx = local.x - b.local.x;
      const dz = local.z - b.local.z;
      if (dx * dx + dz * dz > CFG.landRadius * CFG.landRadius) continue;
      const rel = local.y - b.local.y;
      // Above the shoulder, and resting below the top of the neck (GDD 6.3.1).
      if (rel < CFG.landMinY || rel > CFG.landMaxY) continue;
      return b;
    }
    return null;
  }

  /** @private */
  _onRingLanded(ring, bottle) {
    bottle.ringed = true;
    bottle.body.userData.scored = true;

    // Settle it onto the neck: a frozen dynamic body would still fall, so the
    // ring becomes kinematic and is placed where a ring on a neck belongs.
    const h = ring.handle;
    const snap = this.localToWorld(bottle.local.x, bottle.local.y + CFG.landSnapY, bottle.local.z);
    h.setKinematic(true);
    h.freeze();
    h.setPosition(snap);
    h.setRotation(new THREE.Quaternion());

    this.addScore(bottle.row.points, {
      position: snap,
      label: `${bottle.row.key} bottle`,
      color: bottle.row.key === 'far' ? '#6fe7c4' : '#ffd166',
    });
    audio.play('prize-earned', { volume: 0.7 });
    haptics.play('right', 'score');

    if (bottle.row.directPrize) {
      const kind = bottle.row.directPrize === 'plush-large'
        ? PRIZE_KIND.PLUSH_LARGE
        : PRIZE_KIND.PLUSH_SMALL;
      this.awardDirectPrize(kind);
      this._showPrize(kind, bottle);
    }
    this.updateScoreboard();
  }

  /**
   * Pop the won plush up beside the bottle so the win is visible from the
   * counter. Decor only — it lives in `this.root` and dies with teardown.
   * @private
   */
  _showPrize(kind, bottle) {
    const plush = factory.createPlush(kind === PRIZE_KIND.PLUSH_LARGE ? 'large' : 'small');
    plush.position.set(bottle.local.x, bottle.local.y + 0.42, bottle.local.z);
    plush.scale.setScalar(0.01);
    this.root.add(plush);

    let t = 0;
    const full = kind === PRIZE_KIND.PLUSH_LARGE ? 1.4 : 1;
    this._effects.push((dt) => {
      t += dt;
      plush.scale.setScalar(Math.min(1, t / 0.4) * full);
      plush.rotation.y += dt * 2.0;
      return t < 8;
    });
  }

  /** @private */
  _runEffects(dt) {
    if (this._effects.length === 0) return;
    this._effects = this._effects.filter((fn) => fn(dt));
  }

  /**
   * The round ends once every ring has been thrown and judged, or once the
   * settle window expires with rings still drifting.
   * @private
   */
  _checkRoundEnd(dt) {
    if (!this.roundActive || this.attemptsLeft > 0) return;
    if (this.rings.every((r) => r.resolved)) {
      this.endRound();
      return;
    }
    this._settleTimer += dt;
    if (this._settleTimer >= CFG.settleTime) this.endRound();
  }

  // ── Aim assist (GDD 5.4: 10° cone, the widest in the game) ───────────────

  findAimTarget(releasePos) {
    let best = null;
    let bestD = Infinity;
    for (const b of this.bottles) {
      if (b.ringed) continue;
      const p = this.localToWorld(b.local.x, b.local.y + CFG.landSnapY, b.local.z);
      const d = p.distanceTo(releasePos);
      if (d < bestD) { bestD = d; best = p; }
    }
    return best ? { position: best } : null;
  }

  // ── Test hooks ──────────────────────────────────────────────────────────

  /**
   * `moveTarget` repositions a bottle's physics body, but a fixed body never
   * syncs its mesh (its collider is deliberately offset from the mesh origin),
   * and the narrow cap is a second body the base class knows nothing about.
   * Move all three together or a relocated bottle renders in its old place
   * with half its collision left behind.
   */
  moveTarget(targetId, localPos, rotation) {
    if (!super.moveTarget(targetId, localPos, rotation)) return false;
    const b = this.bottles.find((e) => e.id === targetId);
    if (!b) return true;

    b.local.set(localPos[0], localPos[1], localPos[2]);
    b.mesh.position.copy(b.local);
    // The base class placed the body at the mesh origin; put it back on the
    // body's own centre, and bring the cap with it.
    b.body.setPosition(
      this.localToWorld(b.local.x, b.local.y + CFG.bottleBodyTopY / 2, b.local.z),
    );
    const capHalf = (CFG.bottleCapTopY - CFG.bottleBodyTopY) / 2;
    b.cap.setPosition(
      this.localToWorld(b.local.x, b.local.y + CFG.bottleBodyTopY + capHalf, b.local.z),
    );
    return true;
  }

  /** Diagnostic state for `__cc.stalls.debug()`. */
  debugState() {
    return {
      stallId: this.id,
      bottles: this.bottles.map((b) => ({
        id: b.id, group: b.row.key, ringed: b.ringed, local: b.local.toArray(),
      })),
      rings: this.rings.map((r) => ({
        thrown: !!r.handle.userData.thrown,
        resolved: r.resolved,
        local: r.handle.removed ? null : this.worldToLocal(r.handle.position()).toArray(),
      })),
      ringed: this.bottles.filter((b) => b.ringed).length,
    };
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const ringed = this.bottles.filter((b) => b.ringed);
    const byRow = (key) => ringed.filter((b) => b.row.key === key).length;
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Rings   ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Ringed  ${ringed.length} / ${this.bottles.length}`,
      `N ${byRow('near')}   M ${byRow('mid')}   F ${byRow('far')}`,
    ]);
  }

  onTeardown() {
    this.bottles = [];
    this.rings = [];
    this._effects = [];
  }
}

registerStall({
  id: 'ring-toss',
  name: 'Ring Toss',
  cost: CFG.cost,
  create: (ctx) => new RingToss(ctx),
});

export { RingToss };
